import {
  platformClientIdAtom,
  platformFeaturesAtom,
} from "@framework/shared/atoms";
import { useEffect, useState } from "react";
import {
  buildMobileAppIntentUrl,
  isAndroidBrowserUserAgent,
  normalizeMobileAppDomain,
} from "../utils/mobile-app-links";

/**
 * Resolve the "open in app / download" link for the current tenant.
 *
 * Returns null on the server, before mount, when the effective `mobileApp`
 * flag (`features.mobileApp` from `/platform-features`: release control OR
 * plan feature) is off for this tenant, when the tenant id is unknown, and on any browser that
 * is not a regular Android browser (desktop, iOS, the app's own WebView).
 * Browser checks run only after mount so SSR and hydration markup match.
 *
 * @example
 * const intentUrl = useMobileAppPromotion();
 * if (intentUrl) return <a href={intentUrl}>...</a>;
 */
export function useMobileAppPromotion() {
  const { mobileApp } = platformFeaturesAtom.useValue();
  const clientId = platformClientIdAtom.useValue();
  const [intentUrl, setIntentUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!mobileApp || !clientId) {
      setIntentUrl(null);
      return;
    }

    const domain = normalizeMobileAppDomain(window.location.hostname);

    setIntentUrl(
      domain && isAndroidBrowserUserAgent(navigator.userAgent)
        ? buildMobileAppIntentUrl({ clientId, domain })
        : null,
    );
  }, [mobileApp, clientId]);

  return intentUrl;
}
